import React, { useEffect, useState } from 'react';
import {
  fetchRooms,
  addRoom,
  updateRoom,
  deleteRoom,
  fetchLevels,
  fetchModules,
} from '../services/dataFetch';

const AdministrateRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [levels, setLevels] = useState([]);
  const [modules, setModules] = useState([]);

  const [form, setForm] = useState({
    name: '',
    level_id: '',
    module_id: '',
    capacity: '',
    duration_minutes: '',
  });

  const [message, setMessage] = useState('');
  const [editRoomId, setEditRoomId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadLevels();
    loadModules();
    loadRooms();
  }, []);

  const loadLevels = async () => {
    try {
      const data = await fetchLevels();
      setLevels(data);
    } catch (err) {
      console.error(err);
      setMessage('Failed to load levels');
    }
  };

  const loadModules = async () => {
    try {
      const data = await fetchModules();
      setModules(data);
    } catch (err) {
      console.error(err);
      setMessage('Failed to load modules');
    }
  };

  const loadRooms = async () => {
    try {
      const data = await fetchRooms();
      setRooms(data);
    } catch (err) {
      console.error(err);
      setMessage('Failed to load rooms');
    }
  };

  const handleInputChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleEditInputChange = (e) =>
    setEditForm({ ...editForm, [e.target.name]: e.target.value });

  const handleAddRoom = async (e) => {
    e.preventDefault();

    const roomData = {
      name: form.name,
      level_id: parseInt(form.level_id),
      module_id: parseInt(form.module_id),
      capacity: parseInt(form.capacity),
      duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : 0,
    };

    try {
      await addRoom(roomData);
      setMessage('Room added!');
      setForm({
        name: '',
        level_id: '',
        module_id: '',
        capacity: '',
        duration_minutes: '',
      });
      loadRooms();
    } catch (err) {
      console.error(err);
      setMessage('Error adding room');
    }
  };

  const handleDelete = async (id) => {
    // Single confirmation
    if (!window.confirm('Are you sure you want to delete this room?')) {
      return;
    }

    try {
      // Always use force=true to delete room and its dependencies
      await deleteRoom(id, true);
      setMessage('Room deleted successfully!');
      loadRooms();
    } catch (err) {
      console.error(err);
      const errorMessage = err.message || 'Error deleting room';
      setMessage(errorMessage);
    }
  };

  const handleEdit = (room) => {
    setEditRoomId(room.id);
    setEditForm({
      name: room.name || '',
      level_id: room.level?.id?.toString() || '',
      module_id: room.module?.id?.toString() || '',
      capacity: room.capacity?.toString() || '',
      duration_minutes: room.duration_minutes?.toString() || '',
    });
  };

  const handleUpdateRoom = async (id) => {
    const updatedRoom = {
      name: editForm.name,
      level_id: parseInt(editForm.level_id),
      module_id: parseInt(editForm.module_id),
      capacity: parseInt(editForm.capacity),
      duration_minutes: editForm.duration_minutes ? parseInt(editForm.duration_minutes) : 0,
    };

    try {
      await updateRoom(id, updatedRoom);
      setMessage('Room updated');
      setEditRoomId(null);
      loadRooms();
    } catch (err) {
      console.error(err);
      setMessage('Error updating room');
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2>Administrate Rooms</h2>

      {/* Add Room Form */}
      <form onSubmit={handleAddRoom} style={{ marginBottom: '2rem' }}>
        <input
          type="text"
          name="name"
          placeholder="Room Name"
          value={form.name}
          onChange={handleInputChange}
          required
        />

        <select
          name="level_id"
          value={form.level_id}
          onChange={handleInputChange}
          required
        >
          <option value="">Select Level</option>
          {levels.map((level) => (
            <option key={level.id} value={level.id}>
              {level.name}
            </option>
          ))}
        </select>

        <select
          name="module_id"
          value={form.module_id}
          onChange={handleInputChange}
          required
        >
          <option value="">Select Module</option>
          {modules.map((module) => (
            <option key={module.id} value={module.id}>
              {module.name} ({module.level?.name || 'N/A'})
            </option>
          ))}
        </select>

        <input
          type="number"
          name="capacity"
          placeholder="Number of Students"
          value={form.capacity}
          onChange={handleInputChange}
          required
        />
        <input
          type="number"
          name="duration_minutes"
          placeholder="Duration (min)"
          value={form.duration_minutes}
          onChange={handleInputChange}
        />
        <button type="submit">Add Room</button>
      </form>

      {message && <p>{message}</p>}

      {/* Rooms Table */}
      <table border="1" cellPadding="6" cellSpacing="0" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Room Name</th>
            <th>Level</th>
            <th>Module</th>
            <th>Capacity</th>
            <th>Duration (min)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rooms.map((room) =>
            editRoomId === room.id ? (
              <tr key={room.id}>
                <td>
                  <input
                    name="name"
                    value={editForm.name}
                    onChange={handleEditInputChange}
                  />
                </td>

                <td>
                  <select
                    name="level_id"
                    value={editForm.level_id}
                    onChange={handleEditInputChange}
                  >
                    <option value="">Select Level</option>
                    {levels.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                </td>

                <td>
                  <select
                    name="module_id"
                    value={editForm.module_id}
                    onChange={handleEditInputChange}
                  >
                    <option value="">Select Module</option>
                    {modules.map((module) => (
                      <option key={module.id} value={module.id}>
                        {module.name} ({module.level?.name || 'N/A'})
                      </option>
                    ))}
                  </select>
                </td>

                <td>
                  <input
                    type="number"
                    name="capacity"
                    value={editForm.capacity}
                    onChange={handleEditInputChange}
                  />
                </td>

                <td>
                  <input
                    type="number"
                    name="duration_minutes"
                    value={editForm.duration_minutes}
                    onChange={handleEditInputChange}
                  />
                </td>

                <td>
                  <button onClick={() => handleUpdateRoom(room.id)}>Save</button>
                  <button onClick={() => setEditRoomId(null)}>Cancel</button>
                </td>
              </tr>
            ) : (
              <tr key={room.id}>
                <td>{room.name}</td>
                <td>{room.level?.name || 'N/A'}</td>
                <td>{room.module?.name || 'N/A'}</td>
                <td>{room.capacity}</td>
                <td>{room.duration_minutes}</td>
                <td>
                  <button onClick={() => handleEdit(room)}>Edit</button>
                  <button onClick={() => handleDelete(room.id)}>Delete</button>
                </td>
              </tr>
            )
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdministrateRooms;
